import { z } from "zod";
//#region src/app-adoption-state.ts
/**
* The tenant adoption state of one app for one org (plan `2026-10-06-tenant-app-adoption`, C-1).
*
* The state is a column of core-api's `release.org_app_entitlements` row, `adoption_state`. The
* app is live for the org only in `accepted` (overview section 2). The four states:
*
* - `offered`: the CP licensed the app for the org, and the tenant admin has not accepted it. The
*   operator writes it on a new licence (WS-3b, stage 2).
* - `accepted`: live. The tenant admin's accept or resume writes it, the backfill of migration
*   `release/0020` writes it, and the operator writes it for a standing app (`FIRST_PARTY_APPS`).
* - `paused`: the tenant admin paused the app. A resume returns it to `accepted`.
* - `removed`: the tenant admin removed the app. Only a new accept returns it.
*
* This module is not `app-adoption.ts`. That module holds the CP site-admin adoption matrix (which
* tenants run which version), another meaning of the word on another plane.
*
* Readers: core-api's entitlement service and the `app-adoptions` admin routes (WS-2a), the
* operator's waypoint gate (WS-3a) and the org admin app, through
* `@zion/sdk-dp-admin-contracts` `app-adoptions.ts`. Every export is pure and frozen.
*/
/**
* The four adoption states, in the order of the overview's model table. Closed (index defaults
* row 4, the narrower type): core-api's `CHECK` on `adoption_state` names the same four literals.
*/
const APP_ADOPTION_STATES = Object.freeze([
	"offered",
	"accepted",
	"paused",
	"removed"
]);
/**
* The strict parse of one adoption state. It accepts the four literals of
* {@link APP_ADOPTION_STATES} and refuses every other value: another text, a different case,
* `null` and a non-string. No `.transform()`, so `createOpenApiDocument()` can represent it.
*/
const appAdoptionStateSchema = z.enum(APP_ADOPTION_STATES);
/**
* The four actions a tenant admin takes on an adoption state, one per write route of the
* `app-adoptions` family: `accept`, `pause`, `resume` and `remove`.
*/
const APP_ADOPTION_ACTIONS = Object.freeze([
	"accept",
	"pause",
	"resume",
	"remove"
]);
/** The strict parse of one action: the four literals of {@link APP_ADOPTION_ACTIONS} and nothing else. */
const appAdoptionActionSchema = z.enum(APP_ADOPTION_ACTIONS);
/**
* The adoption state that one stored `adoption_state` column value stands for (D-9, overview
* section 6). Migration `release/0020` (WS-2a, stage 1) adds the column as nullable text with no
* default, and the operator's entitlement INSERTs write no state until WS-3b (stage 2). So in
* stage 1 a new entitlement row stores `NULL`, and `NULL` reads `accepted`: the app stays live for
* the org, as it was before the column existed.
*
* Who calls it: every TypeScript reader of the stored column, before it hands the value to
* {@link isLiveAppAdoptionState} or {@link appAdoptionTransition}. core-api's entitlement service
* and `app-adoptions` routes (WS-2a) call it, and so does the operator (WS-3a) when it reads the
* column in TypeScript. A SQL reader writes the same rule as
* `coalesce(adoption_state, 'accepted') = 'accepted'` while the column is nullable.
*
* Migration `release/0021` (WS-2c, stage 2) sets the column `NOT NULL` with the default `offered`.
* From then on the column never holds `NULL`, the function returns its input, and a SQL reader
* drops the `coalesce`.
*
* @param stored the column value: one of {@link APP_ADOPTION_STATES}, or `null` for a row that
*   stores no state.
* @returns `accepted` for `null`, else `stored` unchanged. A text outside the four states that
*   reaches the function through an unchecked read passes through unchanged, and
*   {@link isLiveAppAdoptionState} then answers `false` for it (refuse over accept). Pure; it never
*   throws.
*/
function appAdoptionStateFromColumn(stored) {
	return stored === null ? "accepted" : stored;
}
/**
* True only for the one live state, `accepted`. Every other value answers `false`: the three other
* states, `null`, `undefined`, an unknown text and a non-string. So a state that a newer writer
* adds reads not live in an older reader (index defaults row 2, refuse over accept, D-2).
*
* Who calls it: a TypeScript reader that holds an adoption state and must decide whether the app is
* live for the org. The value must be a state, never the raw stage-1 column. A stored `NULL` means
* `accepted` (D-9), and this predicate answers `false` for `null`. So a reader of the column first
* passes the value through {@link appAdoptionStateFromColumn}. A SQL reader writes the rule as
* `coalesce(adoption_state, 'accepted') = 'accepted'` while the column is nullable (stage 1), and
* as `adoption_state = 'accepted'` only after migration `release/0021` sets it `NOT NULL`. Pure.
*
* @param value any value: the result of {@link appAdoptionStateFromColumn}, or a field parsed by
*   {@link appAdoptionStateSchema}.
* @returns `true` when `value` is exactly the text `accepted`.
*/
function isLiveAppAdoptionState(value) {
	return value === "accepted";
}
const APP_ADOPTION_ACTION_RULES = Object.freeze({
	accept: Object.freeze({
		to: "accepted",
		from: Object.freeze([
			"offered",
			"removed",
			"accepted"
		])
	}),
	resume: Object.freeze({
		to: "accepted",
		from: Object.freeze(["paused", "accepted"])
	}),
	pause: Object.freeze({
		to: "paused",
		from: Object.freeze(["accepted", "paused"])
	}),
	remove: Object.freeze({
		to: "removed",
		from: Object.freeze([
			"offered",
			"accepted",
			"paused",
			"removed"
		])
	})
});
/**
* The adoption state machine: what one action does to one state. The table, one row per action:
*
* | action | changed | unchanged | conflict |
* |---|---|---|---|
* | `accept` | `offered` and `removed` to `accepted` | `accepted` | `paused` (a paused app takes `resume`) |
* | `resume` | `paused` to `accepted` | `accepted` | `offered`, `removed` (they take `accept`) |
* | `pause` | `accepted` to `paused` | `paused` | `offered`, `removed` |
* | `remove` | `offered`, `accepted` and `paused` to `removed` | `removed` | none |
*
* The machine knows nothing of the licence or of a standing app. core-api checks both before it
* calls this function: a licence-retracted row answers 409 `app_licence_retracted` for every
* action, and a standing app answers 409 `app_standing` for `pause` and `remove`
* (`@zion/sdk-dp-admin-contracts` `APP_ADOPTION_ERROR_CODES`).
*
* Who calls it: core-api's `OrgAppEntitlementService` `accept`, `pause`, `resume` and `remove`
* (WS-2a), and the org admin app, which shows only the actions that do not answer `conflict`.
*
* @param from the row's current state, a member of {@link APP_ADOPTION_STATES}. A value outside
*   the four answers `conflict`, so an unknown state never moves (refuse over accept).
* @param action the action, a member of {@link APP_ADOPTION_ACTIONS}.
* @returns a frozen {@link AppAdoptionTransition}. Pure; it never throws.
*/
function appAdoptionTransition(from, action) {
	const rule = APP_ADOPTION_ACTION_RULES[action];
	if (!rule.from.includes(from)) return Object.freeze({
		outcome: "conflict",
		from,
		action
	});
	if (from === rule.to) return Object.freeze({
		outcome: "unchanged",
		state: from
	});
	return Object.freeze({
		outcome: "changed",
		from,
		to: rule.to
	});
}
/**
* The audit event type that each action emits when its transition is `changed`, one per action:
* `app_adoption.accepted`, `app_adoption.paused`, `app_adoption.resumed` and
* `app_adoption.removed`. Each is a member of `AUDIT_EVENT_CLASSIFICATION` in
* `audit-vocabulary.ts`, level `security`, subject `app`; the `satisfies` clause makes a value
* outside that vocabulary a compile error. The map is frozen.
*/
const APP_ADOPTION_AUDIT_EVENT = Object.freeze({
	accept: "app_adoption.accepted",
	pause: "app_adoption.paused",
	resume: "app_adoption.resumed",
	remove: "app_adoption.removed"
});
//#endregion
export { APP_ADOPTION_ACTIONS, APP_ADOPTION_AUDIT_EVENT, APP_ADOPTION_STATES, appAdoptionActionSchema, appAdoptionStateFromColumn, appAdoptionStateSchema, appAdoptionTransition, isLiveAppAdoptionState };

//# sourceMappingURL=app-adoption-state.js.map